import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Sparkles } from "lucide-react";
import { resolveImageUrl } from "@/lib/images";
import { mix, alpha } from "@/lib/color";

/**
 * Slot Machine — "GAME TIME" cabinet look.
 *
 * A solid 3D box in the merchant's theme colour (top face in perspective,
 * spotlight behind, shadow + colour bounce on the floor) with three recessed
 * light screens in which the prizes scroll (picture when `segment.image_url`
 * is set, otherwise the label).
 *
 * Mechanic (unchanged):
 *  - Backend picks a segment via weighted random (same /play endpoint).
 *  - is_win → all 3 reels land on the SAME winning prize.
 *  - lose   → 3 DIFFERENT prizes (no triple-match).
 */

const toSymbol = (s) => ({
  label: s?.label || "?",
  image: resolveImageUrl(s?.image_url),
  color: s?.color || "#94a3b8",
});

export default function SlotMachine({
  segments = [],
  spinning = false,
  resultIndex = null,
  winLabel = "",
  isWin = false,
  onStart,
  onRevealed,
  themeColor = "#D4AF37",
  disabled = false,
  labelFontFamily = null,
  labelFontWeight = null,
  labelFontScale = 1,
}) {
  const { t } = useTranslation();
  const [armed, setArmed] = useState(false);
  const [reelsSettled, setReelsSettled] = useState([false, false, false]);
  const notifiedRef = useRef(false);

  useEffect(() => {
    if (resultIndex != null && !armed) {
      setArmed(true);
      setReelsSettled([false, false, false]);
      notifiedRef.current = false;
    }
  }, [resultIndex, armed]);

  const symbols = useMemo(() => segments.map(toSymbol), [segments]);

  // Idle: each screen shows a different prize so players see what's at stake.
  const idleSymbols = useMemo(() => {
    if (!symbols.length) return [null, null, null];
    return [0, 1, 2].map((i) => symbols[(i * 2) % symbols.length]);
  }, [symbols]);

  const reels = useMemo(() => {
    if (!symbols.length || resultIndex == null) return [];
    const winner = symbols[resultIndex] || toSymbol(null);

    let finals;
    if (isWin) {
      finals = [winner, winner, winner];
    } else {
      const nonWinners = symbols.filter((_, i) => i !== resultIndex);
      const distinct = [];
      const seen = new Set();
      for (const sym of [...nonWinners, ...symbols]) {
        if (!seen.has(sym.label)) {
          distinct.push(sym);
          seen.add(sym.label);
        }
        if (distinct.length === 3) break;
      }
      while (distinct.length < 3) distinct.push(toSymbol({ label: "✦" }));
      if (
        distinct[0].label === distinct[1].label &&
        distinct[1].label === distinct[2].label
      ) {
        distinct[1] = toSymbol({ label: "✦" });
      }
      finals = distinct;
    }

    const buildStrip = (finalSymbol, seedOffset) => {
      const stripLen = 22;
      const strip = [];
      for (let i = 0; i < stripLen - 1; i++) {
        strip.push(symbols[(resultIndex + i + seedOffset) % symbols.length]);
      }
      strip.push(finalSymbol);
      return strip;
    };

    return finals.map((sym, i) => ({
      strip: buildStrip(sym, i * 3),
      finalSymbol: sym,
      finalColor: isWin ? themeColor : sym.color,
    }));
  }, [symbols, resultIndex, isWin, themeColor]);

  const handleReelSettled = useCallback((i) => {
    setReelsSettled((prev) => {
      if (prev[i]) return prev;
      const next = [...prev];
      next[i] = true;
      return next;
    });
  }, []);

  useEffect(() => {
    if (armed && reelsSettled.every(Boolean) && !notifiedRef.current) {
      notifiedRef.current = true;
      const id = setTimeout(
        () => onRevealed && onRevealed(),
        isWin ? 900 : 700
      );
      return () => clearTimeout(id);
    }
  }, [reelsSettled, armed, isWin, onRevealed]);

  function handleStart() {
    if (spinning || armed || disabled) return;
    notifiedRef.current = false;
    setReelsSettled([false, false, false]);
    onStart && onStart();
  }

  const allSettled = armed && reelsSettled.every(Boolean);
  const celebrate = allSettled && isWin;
  const font = {
    fontFamily: labelFontFamily || "Outfit, ui-sans-serif, system-ui, sans-serif",
    fontWeight: labelFontWeight || 700,
    scale: labelFontScale,
  };

  return (
    <div
      className="relative mx-auto w-full max-w-[480px] px-2 pt-8 pb-12 sm:px-4"
      data-testid="slot-machine-root"
      style={{ "--slot-theme": themeColor }}
    >
      {/* Spotlight behind the cabinet */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -inset-x-16 -top-10 bottom-0 transition-opacity duration-700"
        style={{
          background: `radial-gradient(60% 55% at 50% 38%, ${alpha(
            celebrate ? "#f5c542" : themeColor,
            celebrate ? 0.55 : 0.42
          )} 0%, transparent 72%)`,
          filter: "blur(18px)",
        }}
      />

      {/* Cabinet */}
      <div className="relative">
        {/* Top face (perspective) */}
        <div
          aria-hidden="true"
          className="relative h-7 sm:h-9"
          style={{
            clipPath: "polygon(4.5% 0, 95.5% 0, 100% 100%, 0 100%)",
            background: `linear-gradient(180deg, ${mix(themeColor, 0.42)} 0%, ${mix(
              themeColor,
              0.16
            )} 100%)`,
          }}
        />

        {/* Front face */}
        <div
          className="relative px-4 pt-5 pb-7 sm:px-7 sm:pt-6 sm:pb-9"
          style={{
            background: `linear-gradient(180deg, ${mix(themeColor, 0.04)} 0%, ${themeColor} 45%, ${mix(
              themeColor,
              -0.22
            )} 100%)`,
            boxShadow: `inset 0 1px 0 ${alpha("#ffffff", 0.35)}, inset 4px 0 0 ${alpha(
              "#ffffff",
              0.1
            )}, inset -4px 0 0 rgba(0,0,0,0.16), 0 34px 60px -28px rgba(0,0,0,0.75)`,
            borderRadius: "0 0 8px 8px",
          }}
        >
          {/* Badge */}
          <div className="mb-4 flex justify-center sm:mb-5">
            <div
              className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-[0.34em] text-white/90"
              style={{
                background: "rgba(0,0,0,0.22)",
                boxShadow: "inset 0 1px 0 rgba(255,255,255,0.12)",
              }}
            >
              <Sparkles className="h-3 w-3" />
              {t("play.slot_badge")}
            </div>
          </div>

          {/* Screens */}
          <div className="grid grid-cols-3 gap-2.5 sm:gap-4">
            {[0, 1, 2].map((i) => (
              <Reel
                key={i}
                index={i}
                reel={armed ? reels[i] : null}
                idleSymbol={idleSymbols[i]}
                armed={armed}
                celebrate={celebrate}
                themeColor={themeColor}
                onSettled={() => handleReelSettled(i)}
                font={font}
              />
            ))}
          </div>

          {/* Base: result / progress / start */}
          <div className="mt-5 flex min-h-[48px] items-center justify-center sm:mt-7">
            {allSettled ? (
              <div
                className="text-center text-xs font-bold uppercase tracking-[0.22em] text-white drop-shadow"
                data-testid="slot-result-line"
              >
                {isWin
                  ? t("play.slot_triple_match", { label: winLabel })
                  : t("play.slot_no_match")}
              </div>
            ) : armed ? (
              <div
                className="text-center text-[10px] font-semibold uppercase tracking-[0.26em] text-white/70"
                data-testid="slot-progress"
              >
                {t("play.slot_spinning_reels", {
                  count: reelsSettled.filter(Boolean).length,
                })}
              </div>
            ) : spinning ? (
              <div
                className="inline-flex items-center gap-2.5 rounded-full bg-black/25 px-5 py-2.5 text-xs font-bold uppercase tracking-[0.3em] text-white"
                data-testid="slot-loading"
              >
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-70" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-white" />
                </span>
                {t("play.slot_loading")}
              </div>
            ) : (
              <button
                type="button"
                onClick={handleStart}
                disabled={disabled}
                data-testid="slot-start-btn"
                className="group relative inline-flex items-center gap-2 rounded-full px-7 py-3 text-xs font-extrabold uppercase tracking-[0.22em] transition-transform duration-200 hover:scale-[1.04] active:scale-[0.97] disabled:opacity-60"
                style={{
                  background: "linear-gradient(180deg, #ffffff 0%, #eef0f4 100%)",
                  color: mix(themeColor, -0.45),
                  boxShadow:
                    "0 14px 32px -12px rgba(0,0,0,0.6), inset 0 -2px 0 rgba(0,0,0,0.08), inset 0 1px 0 #fff",
                }}
              >
                <Sparkles className="h-3.5 w-3.5" />
                {t("play.slot_start")}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Floor: dark shadow + colour bounce */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-[4%] bottom-3 h-14"
        style={{
          background: `radial-gradient(50% 60% at 50% 0%, ${alpha(
            themeColor,
            0.55
          )} 0%, rgba(0,0,0,0.5) 45%, transparent 75%)`,
          filter: "blur(10px)",
        }}
      />
    </div>
  );
}

/* ============ Individual reel (recessed light screen) ============ */
function Reel({
  index,
  reel,
  idleSymbol,
  armed,
  celebrate,
  themeColor,
  onSettled,
  font,
}) {
  const screenEl = useRef(null);
  const stripEl = useRef(null);
  const startedRef = useRef(false);
  const onSettledRef = useRef(onSettled);
  onSettledRef.current = onSettled;
  const timersRef = useRef([]);
  const [cellH, setCellH] = useState(0);
  const [phase, setPhase] = useState("idle"); // idle | spinning | landed
  const [blur, setBlur] = useState(false);

  useEffect(() => {
    const el = screenEl.current;
    if (!el) return;
    const measure = () => setCellH(el.clientHeight);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => () => timersRef.current.forEach(clearTimeout), []);

  // Timers are intentionally NOT cleared on re-render (a sibling reel landing
  // re-renders the parent) — only on unmount, see above.
  useEffect(() => {
    if (!armed || !reel || !cellH || startedRef.current) return;
    startedRef.current = true;

    const target = (reel.strip.length - 1) * cellH;
    const duration = 1800 + index * 450;

    if (stripEl.current) {
      stripEl.current.style.transition = "none";
      stripEl.current.style.transform = "translateY(0px)";
      // eslint-disable-next-line no-unused-expressions
      stripEl.current.offsetHeight;
      stripEl.current.style.transition = `transform ${duration}ms cubic-bezier(0.18, 0.52, 0, 1)`;
      stripEl.current.style.transform = `translateY(-${target}px)`;
    }

    setPhase("spinning");
    setBlur(true);
    timersRef.current.push(
      setTimeout(() => setBlur(false), duration * 0.6),
      setTimeout(() => {
        setPhase("landed");
        onSettledRef.current();
      }, duration + 80)
    );
  }, [armed, reel, index, cellH]);

  const landed = phase === "landed";
  const glow = celebrate ? "#f5c542" : reel?.finalColor || themeColor;

  return (
    <div
      className="relative rounded-md p-[5px] sm:p-1.5"
      style={{
        background: "linear-gradient(180deg, #23242a 0%, #0c0d10 100%)",
        boxShadow: landed
          ? `0 0 0 2px ${glow}, 0 0 28px -4px ${glow}, 0 12px 24px -12px rgba(0,0,0,0.9)`
          : "inset 0 1px 0 rgba(255,255,255,0.12), 0 12px 24px -12px rgba(0,0,0,0.9)",
        transition: "box-shadow 320ms ease",
      }}
      data-testid={`slot-reel-${index}`}
      data-phase={phase}
    >
      <div
        ref={screenEl}
        className="relative w-full overflow-hidden rounded-[3px]"
        style={{
          aspectRatio: "5 / 6",
          background: celebrate
            ? "radial-gradient(120% 90% at 50% 40%, #fff8e1 0%, #ffe9a8 60%, #e3c46a 100%)"
            : "radial-gradient(120% 90% at 50% 40%, #ffffff 0%, #eceef2 58%, #b8bcc7 100%)",
          transition: "background 500ms ease",
        }}
      >
        {/* Recess depth */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 z-10"
          style={{
            boxShadow:
              "inset 0 18px 22px -16px rgba(0,0,0,0.55), inset 0 -18px 22px -16px rgba(0,0,0,0.45), inset 0 0 0 1px rgba(0,0,0,0.25)",
          }}
        />

        {/* Strip */}
        <div
          ref={stripEl}
          className="will-change-transform"
          style={{
            transform: "translateY(0px)",
            filter: blur ? "blur(2.5px)" : "blur(0px)",
            transition: "filter 380ms ease",
          }}
        >
          {armed && reel
            ? reel.strip.map((sym, idx) => (
                <Cell
                  key={idx}
                  sym={sym}
                  height={cellH}
                  font={font}
                  emphasis={landed && idx === reel.strip.length - 1}
                />
              ))
            : (
              <Cell sym={idleSymbol} height={cellH} font={font} />
            )}
        </div>
      </div>
    </div>
  );
}

function Cell({ sym, height, font, emphasis = false }) {
  const label = sym?.label || "✦";
  const size = (label.length > 10 ? 12 : label.length > 6 ? 14 : 17) * font.scale;
  return (
    <div
      className="flex items-center justify-center px-1.5 text-center"
      style={{ height: height ? `${height}px` : "100%" }}
    >
      {sym?.image ? (
        <img
          src={sym.image}
          alt={label}
          draggable={false}
          className="max-h-[84%] max-w-[86%] object-contain"
          style={{
            filter: "drop-shadow(0 10px 10px rgba(0,0,0,0.35))",
            transform: emphasis ? "scale(1.06)" : "scale(1)",
            transition: "transform 300ms ease",
          }}
        />
      ) : (
        <div
          className="line-clamp-3 break-words leading-tight"
          style={{
            fontSize: `${size}px`,
            fontFamily: font.fontFamily,
            fontWeight: font.fontWeight,
            color: "#15161c",
            textShadow: "0 1px 0 rgba(255,255,255,0.7)",
          }}
        >
          {label}
        </div>
      )}
    </div>
  );
}
